import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { TextField } from '@mui/material';

// ─── helpers ────────────────────────────────────────────────────────────────

/** Extrae solo dígitos y construye el string de display (p.ej. "12:3") */
const buildDisplay = (digits = '') => {
  if (digits.length === 0) return '';
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
};

/** Construye un valor HH:mm válido y completo, o null si no es posible. */
const buildFull = (digits = '') => {
  if (digits.length < 2) return null;
  const hh = Math.min(parseInt(digits.slice(0, 2), 10), 23);
  const mm = Math.min(parseInt((digits.slice(2, 4) || '00').padEnd(2, '0'), 10), 59);
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

/** Clamp de horas y minutos sobre la cadena de dígitos. */
const clampDigits = (digits = '') => {
  if (digits.length === 0) return '';
  let hh = digits.slice(0, 2);
  let mm = digits.slice(2, 4);
  if (hh.length === 2 && parseInt(hh, 10) > 23) hh = '23';
  if (mm.length === 2 && parseInt(mm, 10) > 59) mm = '59';
  return hh + mm;
};

/** Posición de cursor "correcta" para N dígitos ya escritos (salta el ':'). */
const cursorForDigits = (digits) => (digits.length <= 2 ? digits.length : digits.length + 1);

// ─── componente ─────────────────────────────────────────────────────────────

/**
 * Input de hora en formato 24 h (HH:mm), optimizado para captura rápida.
 *
 * Clave del fix: el estado real vive en "dígitos" (0-4 caracteres numéricos),
 * nunca en el texto formateado ni en la posición de cursor reportada por el
 * navegador. El display y la posición del cursor se DERIVAN siempre de los
 * dígitos, así que no hay carreras entre renders al escribir rápido.
 *
 * Props de control:
 *   value     – string HH:mm o '' (controlado externamente)
 *   onChange  – ({ target: { value } }) => void  → emite HH:mm o ''
 */
const TimeInput24H = ({
  value = '',
  onChange,
  onBlur,
  disabled = false,
  fullWidth = true,
  size = 'small',
  variant = 'outlined',
  label,
  error,
  helperText,
  placeholder = '00:00',
  InputProps = {},
  inputProps = {},
  sx = {},
  ...rest
}) => {
  const [digits, setDigits] = useState((value || '').replace(/\D/g, '').slice(0, 4));
  const inputRef  = useRef(null);
  const isFocused = useRef(false); // evita resincronizar desde el padre mientras el usuario escribe

  const display = buildDisplay(digits);

  // ── sincronizar desde el padre SOLO cuando no hay foco ──────────────────
  useEffect(() => {
    if (!isFocused.current) {
      setDigits((value || '').replace(/\D/g, '').slice(0, 4));
    }
  }, [value]);

  // ── reposicionar cursor SIEMPRE después de los dígitos, de forma síncrona
  // (useLayoutEffect corre antes del paint, sin depender de renders previos)
  useLayoutEffect(() => {
    if (isFocused.current && inputRef.current) {
      const pos = cursorForDigits(digits);
      inputRef.current.setSelectionRange(pos, pos);
    }
  }, [digits]);

  const emit = (val) => onChange?.({ target: { value: val } });

  const commitDigits = (newDigits) => {
    const clamped = clampDigits(newDigits.slice(0, 4));
    setDigits(clamped);
    if (clamped.length === 4) emit(buildFull(clamped));
    else emit('');
  };

  // ── onChange: cualquier edición (escritura, pegado, autocompletado) ─────
  const handleChange = (e) => {
    const rawDigits = (e.target.value ?? '').replace(/\D/g, '').slice(0, 4);
    commitDigits(rawDigits);
  };

  // ── onBlur: completar el valor al salir del campo ────────────────────────
  const handleBlur = (e) => {
    isFocused.current = false;

    if (digits.length === 0) {
      emit('');
    } else {
      const full = buildFull(clampDigits(digits));
      setDigits(full.replace(':', ''));
      emit(full);
    }

    onBlur?.(e);
  };

  const handleFocus = () => {
    isFocused.current = true;
  };

  // ── onKeyDown: backspace controlado en espacio de dígitos ───────────────
  const handleKeyDown = (e) => {
    const selLen = e.target.selectionEnd - e.target.selectionStart;

    if (e.key === 'Backspace') {
      e.preventDefault();
      if (selLen > 0) {
        // selección activa -> borra todo
        commitDigits('');
      } else {
        // borra siempre el último dígito, sin importar dónde esté el cursor
        commitDigits(digits.slice(0, -1));
      }
      return;
    }

    if (e.key === 'Delete') {
      e.preventDefault();
      commitDigits(selLen > 0 ? '' : digits.slice(0, -1));
      return;
    }

    const allowed = [
      'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End',
    ];
    if (allowed.includes(e.key)) return;
    if (e.ctrlKey || e.metaKey) return; // Ctrl+A/C/V/X, etc.

    // Solo dígitos: insertamos nosotros mismos (ignoramos cursor nativo)
    if (/^\d$/.test(e.key)) {
      e.preventDefault();
      commitDigits((digits + e.key).slice(0, 4));
      return;
    }

    // Cualquier otra tecla imprimible: bloquear
    e.preventDefault();
  };

  return (
    <TextField
      {...rest}
      inputRef={inputRef}
      fullWidth={fullWidth}
      disabled={disabled}
      size={size}
      variant={variant}
      label={label}
      error={error}
      helperText={helperText}
      value={display}
      placeholder={placeholder}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      InputProps={InputProps}
      inputProps={{
        inputMode: 'numeric',
        maxLength: 5,
        'aria-label': label ?? 'Hora (formato 24 h)',
        ...inputProps,
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '10px',
          fontFamily: "'DM Sans', sans-serif",
        },
        '& input': {
          fontSize: 14,
          fontWeight: 500,
          letterSpacing: 0.5,
          fontVariantNumeric: 'tabular-nums',
        },
        ...sx,
      }}
    />
  );
};

export default TimeInput24H;